import { useQuery } from "@/lib/query";
import { Link } from "react-router";
import { CalendarCheck, CalendarDays, Stethoscope, Video } from "lucide-react";

import { BookingCard, BookingsLoadError, EmptyBookings } from "@/components/doctor/booking-card";
import { StatCard } from "@/components/common/stat-card";
import { RequireDoctor } from "@/components/doctor/require-doctor";
import { Button } from "@/components/ui/button";
import { bookingsQuery, statsQuery } from "@/lib/api/doctorApi";
import { useAuth } from "@/contexts/AuthContext";
import { useClassifiedBookings } from "@/hooks/doctor/use-classified-bookings";
import { isActiveBooking } from "@/lib/booking-view";
import { bookingSortKey } from "@/lib/format";
import type { Doctor } from "@/types/doctor";
import { usePageMeta } from "@/lib/page-meta";

export function DashboardRoute() {
  usePageMeta({
    title: "Dashboard — HealNova Doctor Portal",
    description:
      "Your HealNova practice at a glance: today's consultations, upcoming appointments and totals.",
  });
  return <RequireDoctor>{(doctor) => <DashboardPage doctor={doctor} />}</RequireDoctor>;
}

function DashboardPage({ doctor }: { doctor: Doctor }) {
  const { uid } = useAuth();
  const stats = useQuery(statsQuery(uid));
  const bookings = useQuery(bookingsQuery(uid));
  const { items, now } = useClassifiedBookings(bookings.data);

  // Only what can still happen: an appointment drops off this list the moment
  // its slot ends (it becomes Missed) or a call is attended (Completed). The
  // clock inside useClassifiedBookings drives that without a reload.
  const upcoming = items
    .filter((item) => isActiveBooking(item.view))
    .sort((a, b) => bookingSortKey(a.booking) - bookingSortKey(b.booking))
    .slice(0, 5);

  // Calculate upcoming locally (booked appointments that haven't ended yet)
  // instead of trusting backend stats which excludes today's booked appointments
  const upcomingCount = items.filter((item) => isActiveBooking(item.view)).length;

  const firstName = doctor.name.replace(/^(dr\.?|prof\.?)\s+/i, "").split(" ")[0] ?? doctor.name;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <header>
        <p className="text-xs font-semibold tracking-[0.18em] text-rose uppercase">Dashboard</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
          Good to see you, Dr. {firstName}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {doctor.specialization}
          {doctor.hospitalName ? ` · ${doctor.hospitalName}` : ""}
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Today"
          value={stats.data?.todayBookings ?? 0}
          icon={CalendarCheck}
          hint="Consultations scheduled today"
          loading={stats.isPending}
        />
        <StatCard
          label="Upcoming"
          value={upcomingCount}
          icon={CalendarDays}
          accent="rose"
          hint="Confirmed future appointments"
          loading={stats.isPending}
        />
        <StatCard
          label="Video"
          value={stats.data?.video ?? 0}
          icon={Video}
          accent="info"
          hint="Remote consultations"
          loading={stats.isPending}
        />
        <StatCard
          label="Total"
          value={stats.data?.totalBookings ?? 0}
          icon={Stethoscope}
          accent="success"
          hint="All-time bookings"
          loading={stats.isPending}
        />
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-semibold">Next appointments</h2>
          <Button asChild variant="ghost" size="sm">
            <Link to="/bookings">View all bookings</Link>
          </Button>
        </div>

        {bookings.isPending ? (
          <div className="space-y-3">
            {[0, 1, 2].map((key) => (
              <div key={key} className="h-28 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : bookings.isError ? (
          // Checked before the empty case: with the list unloaded there is no
          // such thing as "nothing coming up", only "we don't know".
          <BookingsLoadError
            error={bookings.error}
            onRetry={() => void bookings.refetch()}
            retrying={bookings.isFetching}
          />
        ) : upcoming.length === 0 ? (
          <EmptyBookings message="Nothing coming up. Appointments leave this list once their slot ends or the consultation is done — find them under Bookings." />
        ) : (
          <ul className="space-y-3">
            {upcoming.map((item) => (
              <BookingCard
                key={item.booking.id}
                booking={item.booking}
                view={item.view}
                now={now}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
